import React from 'react';
import loadable from '@loadable/component';
import { Route, Switch } from 'react-router-dom';
import { ROUTES, ALLOWED_STATUSES, ENGINEERING_WORK } from 'config/constants';
import { AuthRoute, UnauthRoute, PlatformAdminRoute } from 'components/Routes';

// unauth pages
import LoginPage from '../pages/UnauthPages/LoginPage';
import SignUpPage from '../pages/UnauthPages/SignUpPage';
import ForgotPasswordPage from '../pages/UnauthPages/ForgotPasswordPage';

const CongratulationPages = loadable(
  () =>
    import(
      /* webpackPrefetch: true */ '../pages/AuthPages/CongratulationPages'
    ),
);
//auth pages
const HomePage = loadable(
  () => import(/* webpackPrefetch: true */ '../pages/AuthPages/HomePage'),
);
const AdminApp = loadable(() =>
  import(/* webpackPrefetch: true */ '@anova/admin').then((m) => m.AdminApp),
);
const AdminPatientPages = loadable(
  () =>
    import(
      /* webpackPrefetch: true */ '../pages/PlatformAdmin/AdminPatientPages'
    ),
);
const RepresentativeRegistrationPage = loadable(
  () =>
    import(
      /* webpackPrefetch: true */ '../pages/AuthPages/RepresentativeRegistrationPage'
    ),
);
const ResearchSiteFormPage = loadable(
  () =>
    import(
      /* webpackPrefetch: true */ '../pages/AuthPages/ResearchSiteFormPage'
    ),
);
const SponsorStudyFormPage = loadable(
  () =>
    import(
      /* webpackPrefetch: true */ '../pages/AuthPages/SponsorStudyFormPage'
    ),
);
const PersonnelSponsorStudyFormPage = loadable(
  () =>
    import(
      /* webpackPrefetch: true */ '../pages/AuthPages/PersonnelSponsorStudyFormPage'
    ),
);
const AddStudyManuallyPage = loadable(
  () =>
    import(
      /* webpackPrefetch: true */ '../pages/AuthPages/AddStudyManuallyPage'
    ),
);
const PersonelForm = loadable(
  () => import(/* webpackPrefetch: true */ '../pages/AuthPages/PersonnelForm'),
);

// common
const TestPage = loadable(
  () => import(/* webpackPrefetch: true */ '../pages/CommonPages/TestPage'),
);
const TestFormPage = loadable(
  () => import(/* webpackPrefetch: true */ '../pages/CommonPages/TestFormPage'),
);
const ResetPasswordPage = loadable(
  () =>
    import(
      /* webpackPrefetch: true */ '../pages/CommonPages/ResetPasswordPage'
    ),
);
const ConnectionErrorPage = loadable(
  () =>
    import(
      /* webpackPrefetch: true */ '../pages/CommonPages/ConnectionErrorPage'
    ),
);
const UserDeletedPage = loadable(
  () => import('../pages/CommonPages/UserDeletedPage'),
);
const NotAvailablePage = loadable(
  () => import('../pages/CommonPages/NotAvailablePage'),
);
const VersionPage = loadable(() => import('../pages/CommonPages/VersionPage'));

const ChangeEmailConfirmationPage = loadable(
  () =>
    import(
      /* webpackPrefetch: true */ '../pages/CommonPages/ChangeEmailConfirmationPage/ChangeEmailConfirmationPage.tsx'
    ),
);

const withTracker = (WrappedComponent) => {
  const trackPage = () => {};

  window.initializedAppcues.then((appcues) => {
    try {
      appcues.page();
    } catch (error) {
      console.error('APPCUES INITIALIZATION FAILED');
      console.error(error);
    }
  });

  const HOC = React.forwardRef((props, ref) => {
    trackPage();

    return <WrappedComponent {...props} ref={ref} />;
  });

  HOC.displayName = `withTracker(${
    WrappedComponent.displayName || WrappedComponent.name || 'Component'
  })`;

  return HOC;
};

class Router extends React.PureComponent {
  render() {
    if (ENGINEERING_WORK) {
      return <NotAvailablePage isEngineeringWorks={true} />;
    }

    window.initializedAppcues.then((appcues) => {
      try {
        appcues.anonymous();
      } catch (error) {
        console.error('APPCUES INITIALIZATION FAILED');
        console.error(error);
      }
    });

    return (
      <Switch>
        <PlatformAdminRoute
          path={`${ROUTES.ADMIN_PATIENTS}/new/:patientId/:tab?`}
          component={withTracker(AdminPatientPages)}
        />
        <PlatformAdminRoute
          path={`${ROUTES.ADMIN_PATIENTS}/:patientId`}
          component={withTracker(AdminPatientPages)}
        />
        <PlatformAdminRoute
          path={ROUTES.ADMIN_HOME}
          component={withTracker(AdminApp)}
        />
        <Route
          exact
          path={`${ROUTES.RESET_PASSWORD}/:code`}
          render={(props) => <ResetPasswordPage {...props} />}
        />
        <Route
          exact
          path={ROUTES.TEST_PAGE}
          render={(props) => <TestPage {...props} />}
        />
        <Route
          exact
          path={ROUTES.CONNECTION_ERROR}
          render={(props) => <ConnectionErrorPage {...props} />}
        />
        <Route
          exact
          path={ROUTES.TEST_FORM_PAGE}
          render={(props) => <TestFormPage {...props} />}
        />
        <Route
          exact
          path={ROUTES.DELETED_USER}
          render={(props) => <UserDeletedPage {...props} />}
        />
        <Route
          exact
          path={ROUTES.NOT_AVAILABLE}
          render={(props) => <NotAvailablePage {...props} />}
        />
        <PlatformAdminRoute
          exact
          path={ROUTES.VERSION}
          component={VersionPage}
        />

        {/*Unauth pages*/}
        <UnauthRoute
          exact
          path={ROUTES.LOGIN}
          component={withTracker(LoginPage)}
        />
        <UnauthRoute
          exact
          path={[ROUTES.SIGN_UP, ROUTES.SIGN_UP_INVITE]}
          component={withTracker(SignUpPage)}
        />
        <UnauthRoute
          exact
          path={ROUTES.FORGOT_PASSWORD}
          component={withTracker(ForgotPasswordPage)}
        />

        {/*Auth pages*/}
        <AuthRoute
          path={`${ROUTES.REPRESENTATIVE_REGISTRATION}/:step`}
          component={withTracker(RepresentativeRegistrationPage)}
          allowedStatuses={[ALLOWED_STATUSES.REPRESENTATIVE_STATUS]}
        />
        <AuthRoute
          path={`${ROUTES.RESEARCH_SITE_FORM}/:step`}
          component={withTracker(ResearchSiteFormPage)}
          allowedStatuses={[ALLOWED_STATUSES.RESEARCH_SITE_STATUS]}
        />
        <AuthRoute
          path={`${ROUTES.SPONSOR_STUDY_FORM}/:step`}
          component={withTracker(SponsorStudyFormPage)}
          allowedStatuses={[ALLOWED_STATUSES.SPONSOR_STATUS]}
        />
        <AuthRoute
          path={`${ROUTES.PERSONNEL_SPONSOR_STUDY_FORM}/:step`}
          component={PersonnelSponsorStudyFormPage}
        />
        <AuthRoute
          path={`${ROUTES.ADD_STUDY_MANUALLY_FORM}/:step/:studyId?/:interventionId?`}
          component={withTracker(AddStudyManuallyPage)}
        />
        <AuthRoute
          path={`${ROUTES.CONGRATULATION}`}
          component={withTracker(CongratulationPages)}
        />
        <AuthRoute
          path={`${ROUTES.PERSONNEL_FORM}/:step`}
          component={withTracker(PersonelForm)}
          allowedStatuses={[ALLOWED_STATUSES.PERSONNEL_STATUS]}
        />
        <AuthRoute
          path={ROUTES.CHANGE_EMAIL_CONFIRMATION}
          component={withTracker(ChangeEmailConfirmationPage)}
          allowedStatuses={[
            ALLOWED_STATUSES.GRANTED_VERIFICATION_STATUS,
            ALLOWED_STATUSES.DENIED_VERIFICATION_STATUS,
          ]}
        />

        {/*Auth pages for full registered users*/}
        <AuthRoute
          path={ROUTES.HOME_PAGE}
          component={withTracker(HomePage)}
          allowedStatuses={[
            ALLOWED_STATUSES.GRANTED_VERIFICATION_STATUS,
            ALLOWED_STATUSES.DENIED_VERIFICATION_STATUS,
          ]}
        />
      </Switch>
    );
  }
}

export default Router;
